import type { CanvasOffset } from "@v1/providers/DragAndDropProvider";
import type { SetCandidateNodePayload } from "@v1/reducers/candidate-reducer";
import type { EditorCanvas } from "replo-utils/lib/misc/canvas";

import * as React from "react";

import useDragAndDrop from "@v1/providers/DragAndDropProvider";
import {
  selectCandidateNode,
  selectComponentIdToDrag,
  selectComponentNodeToDrag,
  selectLastCandidateComponentId,
  setCandidateNode as setCandidateNodeAction,
} from "@v1/reducers/candidate-reducer";
import {
  selectDraftComponentIds,
  selectDraftElement_warningThisWillRerenderOnEveryUpdate,
} from "@v1/reducers/core-reducer";
import {
  selectCurrentDragType,
  selectSelectableComponentIds,
} from "@v1/reducers/drag-and-drop-reducer";
import {
  useEditorDispatch,
  useEditorSelector,
  useEditorStore,
} from "@v1/store/store";
import { closestElementToPoint } from "@v2/utils/dom";

import { OFFSET_LIMIT, REPLO_COMPONENT_SELECTOR } from "./canvas-constants";
import {
  selectCanvasDeltaXY,
  selectCanvasInteractionMode,
  selectCanvasScale,
  selectVisibleCanvases,
} from "./canvas-reducer";

function reploElementFromPoint(
  x: number,
  y: number,
  validComponentIds?: Set<string>,
): HTMLElement | null {
  const elements: HTMLElement[] = window.document.elementsFromPoint(
    x,
    y,
  ) as HTMLElement[];

  return (
    elements.find((element) => {
      if (validComponentIds && element.dataset.rid) {
        return validComponentIds.has(element.dataset.rid);
      }
      return element.dataset.rid;
    }) ?? null
  );
}

export const IGNORE_CURSOR_CLASS = "ignore-cursor";

function shouldIgnoreCursor(element: HTMLElement | null) {
  return element?.closest(`.${IGNORE_CURSOR_CLASS}`);
}

export const useSetCandidateNode = () => {
  const { setOffset } = useDragAndDrop();
  const dispatch = useEditorDispatch();
  const store = useEditorStore();

  const setCandidateNode = React.useCallback(
    (
      newCandidateNode: HTMLElement | null,
      candidateCanvas: EditorCanvas | null,
    ) => {
      const lastCandidateComponentId = selectLastCandidateComponentId(
        store.getState(),
      );

      const { deltaX, deltaY } = selectCanvasDeltaXY(store.getState());
      const canvasInteractionMode = selectCanvasInteractionMode(
        store.getState(),
      );
      const isDragging = canvasInteractionMode === "dragging-components";
      const canvasScale = selectCanvasScale(store.getState());
      if (newCandidateNode === null) {
        const payload: SetCandidateNodePayload = {
          candidateNode: null,
          lastCandidateComponentId: null,
          candidateCanvas: null,
        };
        if (!isDragging) {
          payload.componentIdToDrag = null;
          payload.componentNodeToDrag = null;
        }
        dispatch(setCandidateNodeAction(payload));
      } else {
        const componentId = newCandidateNode?.dataset.rid ?? null;

        if (componentId === lastCandidateComponentId) {
          return;
        }

        setOffset?.((offset: CanvasOffset | null) => ({
          x: deltaX,
          y: deltaY,
          scale: canvasScale,
          canvasHeight: offset?.canvasHeight || 0,
          canvasWidth: offset?.canvasHeight || 0,
        }));

        const payload: SetCandidateNodePayload = {
          candidateNode: newCandidateNode,
          candidateCanvas,
          lastCandidateComponentId: componentId,
        };
        if (!isDragging) {
          payload.componentIdToDrag = componentId;
          payload.componentNodeToDrag = newCandidateNode;
        }
        dispatch(setCandidateNodeAction(payload));
      }
    },
    [dispatch, setOffset, store],
  );

  const unsetCandidateNode = React.useCallback(() => {
    setCandidateNode(null, null);
  }, [setCandidateNode]);

  const getCandidateCanvas = React.useCallback(
    (clientX: number, clientY: number) => {
      const visibleCanvases = selectVisibleCanvases(store.getState());
      const candidateCanvasElement = closestElementToPoint(
        Array.from(document.querySelectorAll("[data-canvas-id]")).filter(
          (iframe) => {
            return Object.keys(visibleCanvases).includes(
              (iframe as HTMLElement).dataset.canvasId as EditorCanvas,
            );
          },
        ) as HTMLElement[],
        clientX,
        clientY,
      );

      if (!candidateCanvasElement) {
        return;
      }

      return candidateCanvasElement;
    },
    [store],
  );

  const setCandidateNodeFromDragging = React.useCallback(
    ({ clientX, clientY }: MouseEvent) => {
      const candidateCanvasElement = getCandidateCanvas(clientX, clientY);
      if (!candidateCanvasElement) {
        return;
      }

      let canvasNodeAtPoint = reploElementFromPoint(clientX, clientY);
      if (shouldIgnoreCursor(canvasNodeAtPoint)) {
        return;
      }

      const candidateCanvasId = candidateCanvasElement.dataset.canvasId;

      if (!canvasNodeAtPoint) {
        const frameBounds = candidateCanvasElement.getBoundingClientRect();
        let targetY = clientY;
        let targetX = clientX;
        if (
          clientY < frameBounds.top &&
          clientY > frameBounds.top - OFFSET_LIMIT
        ) {
          targetY = frameBounds.top;
        }
        if (
          clientY > frameBounds.bottom &&
          clientY < frameBounds.bottom + OFFSET_LIMIT
        ) {
          targetY = frameBounds.bottom;
        }
        if (
          clientX < frameBounds.left &&
          clientX > frameBounds.left - OFFSET_LIMIT
        ) {
          targetX = frameBounds.left;
        }
        if (
          clientX > frameBounds.right &&
          clientX < frameBounds.right + OFFSET_LIMIT
        ) {
          targetX = frameBounds.right;
        }
        if (targetX !== clientX || targetY !== clientY) {
          canvasNodeAtPoint = reploElementFromPoint(targetX, targetY);
        }
      }

      let closestComponent: HTMLElement | null = null;

      const dragType = selectCurrentDragType(store.getState());
      if (dragType === "section" && canvasNodeAtPoint) {
        const canvasElement = document.querySelector(
          `[data-canvas-id="${candidateCanvasId}"]`,
        ) as HTMLElement;
        if (!canvasElement) {
          return null;
        }

        const draftElement =
          selectDraftElement_warningThisWillRerenderOnEveryUpdate(
            store.getState(),
          );
        if (!draftElement) {
          return null;
        }

        closestComponent =
          canvasElement.querySelector(
            `[data-rid="${draftElement.component.id}"]`,
          ) ?? null;
      } else {
        closestComponent = canvasNodeAtPoint?.closest(
          REPLO_COMPONENT_SELECTOR,
        ) as HTMLElement | null;
      }

      setCandidateNode(
        closestComponent,
        candidateCanvasId as EditorCanvas | null,
      );
      return closestComponent;
    },
    [getCandidateCanvas, setCandidateNode, store],
  );

  const setCandidateNodeFromEvent = React.useCallback(
    ({
      clientX,
      clientY,
      detail = 1,
      metaKey = false,
      ctrlKey = false,
      shiftKey = false,
    }: {
      clientX: number;
      clientY: number;
      detail?: number;
      metaKey?: boolean;
      ctrlKey?: boolean;
      shiftKey?: boolean;
    }) => {
      const candidateCanvasElement = getCandidateCanvas(clientX, clientY);
      if (!candidateCanvasElement) {
        return;
      }

      const canvasNodeAtPoint = reploElementFromPoint(clientX, clientY);

      if (shouldIgnoreCursor(canvasNodeAtPoint)) {
        return;
      }

      const candidateCanvasId = candidateCanvasElement.dataset.canvasId;

      let closestComponent: HTMLElement | null = null;

      const canvasInteractionMode = selectCanvasInteractionMode(
        store.getState(),
      );
      const allowSelectingAllComponents =
        detail > 1 ||
        metaKey ||
        ctrlKey ||
        shiftKey ||
        canvasInteractionMode === "dragging-components";

      const selectableComponentIds = allowSelectingAllComponents
        ? undefined
        : selectSelectableComponentIds(store.getState());

      const closestValidNode = reploElementFromPoint(
        clientX,
        clientY,
        selectableComponentIds,
      );
      closestComponent = closestValidNode;

      setCandidateNode(
        closestComponent,
        candidateCanvasId as EditorCanvas | null,
      );
      return closestComponent;
    },
    [getCandidateCanvas, setCandidateNode, store],
  );

  return {
    setCandidateNodeFromEvent,
    setCandidateNodeFromDragging,
    unsetCandidateNode,
  };
};

export function useCandidateNode() {
  const draftComponentIds = useEditorSelector(selectDraftComponentIds);

  const candidateNode = useEditorSelector(selectCandidateNode);
  const componentIdToDrag = useEditorSelector(selectComponentIdToDrag);
  const componentNodeToDrag = useEditorSelector(selectComponentNodeToDrag);

  let isCandidateBoxVisible = true;
  if (draftComponentIds.length === 1) {
    isCandidateBoxVisible = componentIdToDrag
      ? !draftComponentIds.includes(componentIdToDrag)
      : false;
  }

  const shouldShowCandidateBox = componentNodeToDrag && isCandidateBoxVisible;

  return {
    candidateNode,
    shouldShowCandidateBox,
  };
}
